import { Locale } from "@/lib/i18n";

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

type Dictionary = {
  ui: {
    previous: string;
    next: string;
    previousTestimonial: string;
    nextTestimonial: string;
    goToTestimonial: string;
    pageNotFound: string;
    pageNotFoundBody: string;
    backToHome: string;
  };
  uploader: {
    title: string;
    description: string;
    supports: string;
    supportsAll: string;
  };
  nav: {
    home: string;
    allTools: string;
    blog: string;
    about: string;
    faq: string;
    exploreTools: string;
    language: string;
  };
  footer: {
    home: string;
    allTools: string;
    blog: string;
    about: string;
    contact: string;
    contactUs: string;
    email: string;
    subscribe: string;
    privacy: string;
    copyright: string;
  };
  cookie: {
    title: string;
    body: string;
    reject: string;
    accept: string;
    privacy: string;
  };
  home: {
    growthTitle: string;
    growthDescription: string;
    whyMatters: string;
    growthHeadline: string;
    growthP1: string;
    growthP2: string;
    explore: string;
    readBlog: string;
    privacy: string;
    nextStep: string;
    nextStepBody: string;
  };
};

const en: Dictionary = {
  ui: {
    previous: "Previous",
    next: "Next",
    previousTestimonial: "Previous testimonial",
    nextTestimonial: "Next testimonial",
    goToTestimonial: "Go to testimonial",
    pageNotFound: "Page Not Found",
    pageNotFoundBody: "The page you are looking for doesn't exist or has been moved.",
    backToHome: "Back to Home",
  },
  uploader: {
    title: "Upload your files here",
    description: "Drag & drop or click to browse",
    supports: "Supports",
    supportsAll: "Supports all file types",
  },
  nav: {
    home: "Home",
    allTools: "All Tools",
    blog: "Blog",
    about: "About",
    faq: "FAQ",
    exploreTools: "Explore Tools",
    language: "Language",
  },
  footer: {
    home: "Home",
    allTools: "All Tools",
    blog: "Blog",
    about: "About",
    contact: "Contact",
    contactUs: "Contact us:",
    email: "Email",
    subscribe: "Subscribe to news",
    privacy: "Privacy Policy",
    copyright: "© 2025 dogufy.com. All Rights Reserved.",
  },
  cookie: {
    title: "Cookie consent",
    body:
      "We only enable analytics after you agree. Necessary storage stays active for security and core site behavior.",
    reject: "Reject analytics",
    accept: "Accept analytics",
    privacy: "Privacy Policy",
  },
  home: {
    growthTitle: "Built For Growth",
    growthDescription:
      "This website is now positioned not just as a free tool directory, but as a structured content platform that can grow through search, multilingual landing pages, and AI discovery.",
    whyMatters: "Why This Matters",
    growthHeadline:
      "Stronger content depth gives Dogufy a better chance to earn rankings, snippets, and AI citations.",
    growthP1:
      "Search engines do not only rank tools based on the converter itself. They also evaluate topical coverage, clarity of page intent, trust signals, internal linking, and whether the site helps users complete the task.",
    growthP2:
      "That is why we are adding richer supporting sections, multilingual entry points, consent-aware analytics, and an editorial layer that can publish practical articles around real document workflows.",
    explore: "Explore all tools",
    readBlog: "Read the blog",
    privacy: "Privacy policy",
    nextStep: "Recommended next step:",
    nextStepBody:
      "The biggest ranking gain from here will come from translating core page copy for the highest-priority locales, expanding tool-specific landing pages, and publishing blog posts that answer precise long-tail searches.",
  },
};

const id: Dictionary = {
  ui: {
    previous: "Sebelumnya",
    next: "Berikutnya",
    previousTestimonial: "Testimoni sebelumnya",
    nextTestimonial: "Testimoni berikutnya",
    goToTestimonial: "Buka testimoni",
    pageNotFound: "Halaman Tidak Ditemukan",
    pageNotFoundBody: "Halaman yang kamu cari tidak ada atau sudah dipindahkan.",
    backToHome: "Kembali ke Beranda",
  },
  uploader: {
    title: "Upload file kamu di sini",
    description: "Tarik dan lepas atau klik untuk memilih file",
    supports: "Mendukung",
    supportsAll: "Mendukung semua tipe file",
  },
  nav: {
    home: "Beranda",
    allTools: "Semua Tools",
    blog: "Blog",
    about: "Tentang",
    faq: "FAQ",
    exploreTools: "Jelajahi Tools",
    language: "Bahasa",
  },
  footer: {
    home: "Beranda",
    allTools: "Semua Tools",
    blog: "Blog",
    about: "Tentang",
    contact: "Kontak",
    contactUs: "Hubungi kami:",
    email: "Email",
    subscribe: "Langganan kabar terbaru",
    privacy: "Kebijakan Privasi",
    copyright: "© 2025 dogufy.com. Semua Hak Dilindungi.",
  },
  cookie: {
    title: "Persetujuan cookie",
    body:
      "Analitik hanya akan aktif setelah kamu menyetujui. Penyimpanan penting tetap aktif untuk keamanan dan fungsi inti website.",
    reject: "Tolak analitik",
    accept: "Terima analitik",
    privacy: "Kebijakan Privasi",
  },
  home: {
    growthTitle: "Dibangun Untuk Bertumbuh",
    growthDescription:
      "Website ini sekarang diposisikan bukan hanya sebagai kumpulan tools gratis, tetapi juga sebagai platform konten terstruktur yang bisa tumbuh melalui search, landing page multibahasa, dan discovery dari AI.",
    whyMatters: "Kenapa Ini Penting",
    growthHeadline:
      "Kedalaman konten yang lebih kuat memberi Dogufy peluang lebih besar untuk mendapatkan ranking, snippet, dan sitasi dari AI.",
    growthP1:
      "Search engine tidak hanya menilai tools dari fitur converternya saja. Mereka juga melihat cakupan topik, kejelasan intent halaman, sinyal kepercayaan, internal linking, dan apakah website benar-benar membantu pengguna menyelesaikan tugasnya.",
    growthP2:
      "Karena itu kita menambahkan section pendukung yang lebih kaya, entry point multibahasa, analytics berbasis consent, dan layer editorial yang bisa menerbitkan artikel praktis seputar workflow dokumen nyata.",
    explore: "Lihat semua tools",
    readBlog: "Baca blog",
    privacy: "Kebijakan privasi",
    nextStep: "Langkah berikutnya yang disarankan:",
    nextStepBody:
      "Kenaikan ranking terbesar setelah ini akan datang dari translasi copy inti untuk locale prioritas, memperluas landing page per tool, dan menerbitkan artikel blog yang menjawab long-tail search dengan intent yang sangat jelas.",
  },
};

export function getDictionary(locale: Locale): Dictionary {
  if (locale === "id") {
    return id;
  }

  const override = localeOverrides[locale];
  if (override) {
    return mergeDictionary(en, override);
  }
  return en;
}

const localeOverrides: Partial<Record<Locale, DeepPartial<Dictionary>>> = {
  vi: {
    ui: {
      previous: "Trước",
      next: "Tiếp theo",
      previousTestimonial: "Lời chứng thực trước",
      nextTestimonial: "Lời chứng thực tiếp theo",
      goToTestimonial: "Đi đến lời chứng thực",
      pageNotFound: "Không Tìm Thấy Trang",
      pageNotFoundBody: "Trang bạn tìm kiếm không tồn tại hoặc đã được chuyển đi.",
      backToHome: "Quay Về Trang Chủ",
    },
    uploader: {
      title: "Tải tệp của bạn lên đây",
      description: "Kéo thả hoặc bấm để chọn tệp",
      supports: "Hỗ trợ",
      supportsAll: "Hỗ trợ mọi loại tệp",
    },
    nav: {
      home: "Trang chủ",
      allTools: "Tất cả công cụ",
      blog: "Blog",
      about: "Giới thiệu",
      faq: "FAQ",
      exploreTools: "Khám phá công cụ",
      language: "Ngôn ngữ",
    },
    cookie: {
      title: "Chấp thuận cookie",
      body: "Phân tích chỉ được bật sau khi bạn đồng ý. Bộ nhớ cần thiết vẫn hoạt động cho bảo mật và chức năng cốt lõi của trang web.",
      reject: "Từ chối phân tích",
      accept: "Chấp nhận phân tích",
      privacy: "Chính sách quyền riêng tư",
    },
  },
  cs: {
    ui: {
      previous: "Předchozí",
      next: "Další",
      previousTestimonial: "Předchozí reference",
      nextTestimonial: "Další reference",
      goToTestimonial: "Přejít na referenci",
      pageNotFound: "Stránka Nenalezena",
      pageNotFoundBody: "Stránka, kterou hledáte, neexistuje nebo byla přesunuta.",
      backToHome: "Zpět na úvod",
    },
    uploader: {
      title: "Nahrajte své soubory sem",
      description: "Přetáhněte soubory nebo klikněte pro výběr",
      supports: "Podporuje",
      supportsAll: "Podporuje všechny typy souborů",
    },
    nav: {
      home: "Domů",
      allTools: "Všechny nástroje",
      blog: "Blog",
      about: "O nás",
      faq: "FAQ",
      exploreTools: "Prozkoumat nástroje",
      language: "Jazyk",
    },
    cookie: {
      title: "Souhlas s cookies",
      body: "Analytiku zapneme až po vašem souhlasu. Nezbytné úložiště zůstává aktivní kvůli bezpečnosti a základní funkčnosti webu.",
      reject: "Odmítnout analytiku",
      accept: "Přijmout analytiku",
      privacy: "Zásady ochrany osobních údajů",
    },
  },
  es: {
    ui: {
      previous: "Anterior",
      next: "Siguiente",
      previousTestimonial: "Testimonio anterior",
      nextTestimonial: "Siguiente testimonio",
      goToTestimonial: "Ir al testimonio",
      pageNotFound: "Página No Encontrada",
      pageNotFoundBody: "La página que buscas no existe o ha sido movida.",
      backToHome: "Volver al inicio",
    },
    uploader: {
      title: "Sube tus archivos aquí",
      description: "Arrastra y suelta o haz clic para explorar",
      supports: "Compatible con",
      supportsAll: "Compatible con todos los tipos de archivo",
    },
    nav: {
      home: "Inicio",
      allTools: "Todas las herramientas",
      blog: "Blog",
      about: "Acerca de",
      faq: "FAQ",
      exploreTools: "Explorar herramientas",
      language: "Idioma",
    },
    cookie: {
      title: "Consentimiento de cookies",
      body: "Solo activamos la analítica después de tu consentimiento. El almacenamiento necesario permanece activo para la seguridad y el funcionamiento básico del sitio.",
      reject: "Rechazar analítica",
      accept: "Aceptar analítica",
      privacy: "Política de privacidad",
    },
  },
  fr: {
    ui: {
      previous: "Précédent",
      next: "Suivant",
      previousTestimonial: "Témoignage précédent",
      nextTestimonial: "Témoignage suivant",
      goToTestimonial: "Aller au témoignage",
      pageNotFound: "Page Introuvable",
      pageNotFoundBody: "La page que vous recherchez n'existe pas ou a été déplacée.",
      backToHome: "Retour à l'accueil",
    },
    uploader: {
      title: "Téléversez vos fichiers ici",
      description: "Glissez-déposez ou cliquez pour parcourir",
      supports: "Prend en charge",
      supportsAll: "Prend en charge tous les types de fichiers",
    },
    nav: {
      home: "Accueil",
      allTools: "Tous les outils",
      blog: "Blog",
      about: "À propos",
      faq: "FAQ",
      exploreTools: "Explorer les outils",
      language: "Langue",
    },
    cookie: {
      title: "Consentement aux cookies",
      body: "Les analyses ne sont activées qu'après votre accord. Le stockage nécessaire reste actif pour la sécurité et le fonctionnement essentiel du site.",
      reject: "Refuser l'analyse",
      accept: "Accepter l'analyse",
      privacy: "Politique de confidentialité",
    },
  },
  de: {
    ui: {
      previous: "Zurück",
      next: "Weiter",
      previousTestimonial: "Vorherige Bewertung",
      nextTestimonial: "Nächste Bewertung",
      goToTestimonial: "Zur Bewertung gehen",
      pageNotFound: "Seite Nicht Gefunden",
      pageNotFoundBody: "Die gesuchte Seite existiert nicht oder wurde verschoben.",
      backToHome: "Zur Startseite",
    },
    uploader: {
      title: "Dateien hier hochladen",
      description: "Dateien ziehen oder zum Auswählen klicken",
      supports: "Unterstützt",
      supportsAll: "Unterstützt alle Dateitypen",
    },
    nav: {
      home: "Startseite",
      allTools: "Alle Tools",
      blog: "Blog",
      about: "Über uns",
      faq: "FAQ",
      exploreTools: "Tools entdecken",
      language: "Sprache",
    },
    cookie: {
      title: "Cookie-Einwilligung",
      body: "Analysen werden erst nach Ihrer Zustimmung aktiviert. Notwendiger Speicher bleibt für Sicherheit und Kernfunktionen der Website aktiv.",
      reject: "Analyse ablehnen",
      accept: "Analyse akzeptieren",
      privacy: "Datenschutzerklärung",
    },
  },
  it: {
    ui: {
      previous: "Precedente",
      next: "Successivo",
      previousTestimonial: "Testimonianza precedente",
      nextTestimonial: "Testimonianza successiva",
      goToTestimonial: "Vai alla testimonianza",
      pageNotFound: "Pagina Non Trovata",
      pageNotFoundBody: "La pagina che stai cercando non esiste o è stata spostata.",
      backToHome: "Torna alla home",
    },
    uploader: {
      title: "Carica qui i tuoi file",
      description: "Trascina i file o fai clic per selezionare",
      supports: "Supporta",
      supportsAll: "Supporta tutti i tipi di file",
    },
    nav: {
      home: "Home",
      allTools: "Tutti gli strumenti",
      blog: "Blog",
      about: "Chi siamo",
      faq: "FAQ",
      exploreTools: "Esplora strumenti",
      language: "Lingua",
    },
    cookie: {
      title: "Consenso ai cookie",
      body: "Attiviamo l'analisi solo dopo il tuo consenso. L'archiviazione necessaria rimane attiva per la sicurezza e il funzionamento essenziale del sito.",
      reject: "Rifiuta analisi",
      accept: "Accetta analisi",
      privacy: "Informativa sulla privacy",
    },
  },
  hu: {
    ui: {
      previous: "Előző",
      next: "Következő",
      previousTestimonial: "Előző vélemény",
      nextTestimonial: "Következő vélemény",
      goToTestimonial: "Ugrás a véleményhez",
      pageNotFound: "Az Oldal Nem Található",
      pageNotFoundBody: "A keresett oldal nem létezik vagy áthelyezték.",
      backToHome: "Vissza a főoldalra",
    },
    uploader: {
      title: "Töltsd fel ide a fájljaidat",
      description: "Húzd ide a fájlokat vagy kattints a tallózáshoz",
      supports: "Támogatott",
      supportsAll: "Minden fájltípus támogatott",
    },
    nav: {
      home: "Kezdőlap",
      allTools: "Összes eszköz",
      blog: "Blog",
      about: "Rólunk",
      faq: "GYIK",
      exploreTools: "Eszközök felfedezése",
      language: "Nyelv",
    },
    cookie: {
      title: "Cookie-hozzájárulás",
      body: "Az analitika csak a hozzájárulásod után aktiválódik. A szükséges tárolás a biztonság és az alapműködés miatt aktív marad.",
      reject: "Analitika elutasítása",
      accept: "Analitika elfogadása",
      privacy: "Adatvédelmi szabályzat",
    },
  },
  ms: {
    ui: {
      previous: "Sebelumnya",
      next: "Seterusnya",
      previousTestimonial: "Testimoni sebelumnya",
      nextTestimonial: "Testimoni seterusnya",
      goToTestimonial: "Pergi ke testimoni",
      pageNotFound: "Halaman Tidak Ditemui",
      pageNotFoundBody: "Halaman yang anda cari tidak wujud atau telah dipindahkan.",
      backToHome: "Kembali ke utama",
    },
    uploader: {
      title: "Muat naik fail anda di sini",
      description: "Seret dan lepas atau klik untuk memilih fail",
      supports: "Menyokong",
      supportsAll: "Menyokong semua jenis fail",
    },
    nav: {
      home: "Utama",
      allTools: "Semua alat",
      blog: "Blog",
      about: "Tentang",
      faq: "FAQ",
      exploreTools: "Teroka alat",
      language: "Bahasa",
    },
    cookie: {
      title: "Persetujuan kuki",
      body: "Analitik hanya diaktifkan selepas anda bersetuju. Storan penting kekal aktif untuk keselamatan dan fungsi teras laman web.",
      reject: "Tolak analitik",
      accept: "Terima analitik",
      privacy: "Dasar privasi",
    },
  },
  nl: {
    ui: {
      previous: "Vorige",
      next: "Volgende",
      previousTestimonial: "Vorige testimonial",
      nextTestimonial: "Volgende testimonial",
      goToTestimonial: "Ga naar testimonial",
      pageNotFound: "Pagina Niet Gevonden",
      pageNotFoundBody: "De pagina die je zoekt bestaat niet of is verplaatst.",
      backToHome: "Terug naar home",
    },
    uploader: {
      title: "Upload hier je bestanden",
      description: "Sleep bestanden hierheen of klik om te bladeren",
      supports: "Ondersteunt",
      supportsAll: "Ondersteunt alle bestandstypen",
    },
    nav: {
      home: "Home",
      allTools: "Alle tools",
      blog: "Blog",
      about: "Over",
      faq: "FAQ",
      exploreTools: "Tools verkennen",
      language: "Taal",
    },
    cookie: {
      title: "Cookie-toestemming",
      body: "Analyse wordt alleen ingeschakeld nadat je toestemming geeft. Noodzakelijke opslag blijft actief voor beveiliging en basisfunctionaliteit van de website.",
      reject: "Analyse weigeren",
      accept: "Analyse accepteren",
      privacy: "Privacybeleid",
    },
  },
  pl: {
    ui: {
      previous: "Poprzedni",
      next: "Następny",
      previousTestimonial: "Poprzednia opinia",
      nextTestimonial: "Następna opinia",
      goToTestimonial: "Przejdź do opinii",
      pageNotFound: "Nie Znaleziono Strony",
      pageNotFoundBody: "Strona, której szukasz, nie istnieje lub została przeniesiona.",
      backToHome: "Powrót do strony głównej",
    },
    uploader: {
      title: "Prześlij tutaj swoje pliki",
      description: "Przeciągnij i upuść lub kliknij, aby wybrać",
      supports: "Obsługuje",
      supportsAll: "Obsługuje wszystkie typy plików",
    },
    nav: {
      home: "Strona główna",
      allTools: "Wszystkie narzędzia",
      blog: "Blog",
      about: "O nas",
      faq: "FAQ",
      exploreTools: "Przeglądaj narzędzia",
      language: "Język",
    },
    cookie: {
      title: "Zgoda na pliki cookie",
      body: "Analityka jest włączana dopiero po Twojej zgodzie. Niezbędne dane pozostają aktywne dla bezpieczeństwa i podstawowego działania strony.",
      reject: "Odrzuć analitykę",
      accept: "Akceptuj analitykę",
      privacy: "Polityka prywatności",
    },
  },
  pt: {
    ui: {
      previous: "Anterior",
      next: "Seguinte",
      previousTestimonial: "Testemunho anterior",
      nextTestimonial: "Próximo testemunho",
      goToTestimonial: "Ir para testemunho",
      pageNotFound: "Página Não Encontrada",
      pageNotFoundBody: "A página que procura não existe ou foi movida.",
      backToHome: "Voltar ao início",
    },
    uploader: {
      title: "Carregue os seus ficheiros aqui",
      description: "Arraste e largue ou clique para procurar",
      supports: "Suporta",
      supportsAll: "Suporta todos os tipos de ficheiro",
    },
    nav: {
      home: "Início",
      allTools: "Todas as ferramentas",
      blog: "Blog",
      about: "Sobre",
      faq: "FAQ",
      exploreTools: "Explorar ferramentas",
      language: "Idioma",
    },
    cookie: {
      title: "Consentimento de cookies",
      body: "A análise só é ativada depois do seu consentimento. O armazenamento necessário permanece ativo para segurança e funcionamento essencial do site.",
      reject: "Rejeitar análise",
      accept: "Aceitar análise",
      privacy: "Política de privacidade",
    },
  },
  th: {
    ui: {
      previous: "ก่อนหน้า",
      next: "ถัดไป",
      previousTestimonial: "รีวิวก่อนหน้า",
      nextTestimonial: "รีวิวถัดไป",
      goToTestimonial: "ไปที่รีวิว",
      pageNotFound: "ไม่พบหน้า",
      pageNotFoundBody: "หน้าที่คุณกำลังค้นหาไม่มีอยู่หรือถูกย้ายแล้ว",
      backToHome: "กลับหน้าแรก",
    },
    uploader: {
      title: "อัปโหลดไฟล์ของคุณที่นี่",
      description: "ลากวางหรือคลิกเพื่อเลือกไฟล์",
      supports: "รองรับ",
      supportsAll: "รองรับไฟล์ทุกประเภท",
    },
    nav: {
      home: "หน้าแรก",
      allTools: "เครื่องมือทั้งหมด",
      blog: "บล็อก",
      about: "เกี่ยวกับ",
      faq: "คำถามที่พบบ่อย",
      exploreTools: "สำรวจเครื่องมือ",
      language: "ภาษา",
    },
    cookie: {
      title: "การยินยอมคุกกี้",
      body: "เราจะเปิดใช้การวิเคราะห์หลังจากคุณยินยอมเท่านั้น พื้นที่จัดเก็บที่จำเป็นจะยังคงทำงานเพื่อความปลอดภัยและฟังก์ชันหลักของเว็บไซต์",
      reject: "ปฏิเสธการวิเคราะห์",
      accept: "ยอมรับการวิเคราะห์",
      privacy: "นโยบายความเป็นส่วนตัว",
    },
  },
  tr: {
    ui: {
      previous: "Önceki",
      next: "Sonraki",
      previousTestimonial: "Önceki yorum",
      nextTestimonial: "Sonraki yorum",
      goToTestimonial: "Yoruma git",
      pageNotFound: "Sayfa Bulunamadı",
      pageNotFoundBody: "Aradığınız sayfa mevcut değil veya taşınmış.",
      backToHome: "Ana sayfaya dön",
    },
    uploader: {
      title: "Dosyalarınızı buraya yükleyin",
      description: "Sürükleyip bırakın veya seçmek için tıklayın",
      supports: "Destekler",
      supportsAll: "Tüm dosya türlerini destekler",
    },
    nav: {
      home: "Ana sayfa",
      allTools: "Tüm araçlar",
      blog: "Blog",
      about: "Hakkında",
      faq: "SSS",
      exploreTools: "Araçları keşfet",
      language: "Dil",
    },
    cookie: {
      title: "Çerez onayı",
      body: "Analitik yalnızca onayınızdan sonra etkinleşir. Gerekli depolama, güvenlik ve temel site işlevleri için aktif kalır.",
      reject: "Analitiği reddet",
      accept: "Analitiği kabul et",
      privacy: "Gizlilik politikası",
    },
  },
  el: {
    ui: {
      previous: "Προηγούμενο",
      next: "Επόμενο",
      previousTestimonial: "Προηγούμενη μαρτυρία",
      nextTestimonial: "Επόμενη μαρτυρία",
      goToTestimonial: "Μετάβαση στη μαρτυρία",
      pageNotFound: "Η Σελίδα Δεν Βρέθηκε",
      pageNotFoundBody: "Η σελίδα που αναζητάτε δεν υπάρχει ή έχει μετακινηθεί.",
      backToHome: "Επιστροφή στην αρχική",
    },
    uploader: {
      title: "Ανεβάστε τα αρχεία σας εδώ",
      description: "Σύρετε και αφήστε ή κάντε κλικ για επιλογή",
      supports: "Υποστηρίζει",
      supportsAll: "Υποστηρίζει όλους τους τύπους αρχείων",
    },
    nav: {
      home: "Αρχική",
      allTools: "Όλα τα εργαλεία",
      blog: "Blog",
      about: "Σχετικά",
      faq: "FAQ",
      exploreTools: "Εξερεύνηση εργαλείων",
      language: "Γλώσσα",
    },
    cookie: {
      title: "Συναίνεση cookies",
      body: "Τα αναλυτικά στοιχεία ενεργοποιούνται μόνο μετά τη συγκατάθεσή σας. Η απαραίτητη αποθήκευση παραμένει ενεργή για ασφάλεια και βασική λειτουργία του ιστότοπου.",
      reject: "Απόρριψη αναλυτικών",
      accept: "Αποδοχή αναλυτικών",
      privacy: "Πολιτική απορρήτου",
    },
  },
  uk: {
    ui: {
      previous: "Попередній",
      next: "Наступний",
      previousTestimonial: "Попередній відгук",
      nextTestimonial: "Наступний відгук",
      goToTestimonial: "Перейти до відгуку",
      pageNotFound: "Сторінку Не Знайдено",
      pageNotFoundBody: "Сторінка, яку ви шукаєте, не існує або була переміщена.",
      backToHome: "Повернутися на головну",
    },
    uploader: {
      title: "Завантажте свої файли сюди",
      description: "Перетягніть файл або натисніть для вибору",
      supports: "Підтримує",
      supportsAll: "Підтримує всі типи файлів",
    },
    nav: {
      home: "Головна",
      allTools: "Усі інструменти",
      blog: "Блог",
      about: "Про нас",
      faq: "FAQ",
      exploreTools: "Переглянути інструменти",
      language: "Мова",
    },
    cookie: {
      title: "Згода на cookie",
      body: "Аналітика вмикається лише після вашої згоди. Необхідне сховище залишається активним для безпеки та основної роботи сайту.",
      reject: "Відхилити аналітику",
      accept: "Прийняти аналітику",
      privacy: "Політика конфіденційності",
    },
  },
  ru: {
    ui: {
      previous: "Назад",
      next: "Далее",
      previousTestimonial: "Предыдущий отзыв",
      nextTestimonial: "Следующий отзыв",
      goToTestimonial: "Перейти к отзыву",
      pageNotFound: "Страница Не Найдена",
      pageNotFoundBody: "Страница, которую вы ищете, не существует или была перемещена.",
      backToHome: "Вернуться на главную",
    },
    uploader: {
      title: "Загрузите файлы сюда",
      description: "Перетащите файлы или нажмите для выбора",
      supports: "Поддерживает",
      supportsAll: "Поддерживает все типы файлов",
    },
    nav: {
      home: "Главная",
      allTools: "Все инструменты",
      blog: "Блог",
      about: "О нас",
      faq: "FAQ",
      exploreTools: "Изучить инструменты",
      language: "Язык",
    },
    cookie: {
      title: "Согласие на cookie",
      body: "Аналитика включается только после вашего согласия. Необходимое хранилище остается активным для безопасности и базовой работы сайта.",
      reject: "Отклонить аналитику",
      accept: "Принять аналитику",
      privacy: "Политика конфиденциальности",
    },
  },
  ar: {
    ui: {
      previous: "السابق",
      next: "التالي",
      previousTestimonial: "الشهادة السابقة",
      nextTestimonial: "الشهادة التالية",
      goToTestimonial: "اذهب إلى الشهادة",
      pageNotFound: "الصفحة غير موجودة",
      pageNotFoundBody: "الصفحة التي تبحث عنها غير موجودة أو تم نقلها.",
      backToHome: "العودة إلى الرئيسية",
    },
    uploader: {
      title: "ارفع ملفاتك هنا",
      description: "اسحب وأفلت أو انقر للاختيار",
      supports: "يدعم",
      supportsAll: "يدعم جميع أنواع الملفات",
    },
    nav: {
      home: "الرئيسية",
      allTools: "كل الأدوات",
      blog: "المدونة",
      about: "حول",
      faq: "الأسئلة الشائعة",
      exploreTools: "استكشف الأدوات",
      language: "اللغة",
    },
    cookie: {
      title: "موافقة ملفات تعريف الارتباط",
      body: "لا يتم تفعيل التحليلات إلا بعد موافقتك. يبقى التخزين الضروري نشطًا للأمان والوظائف الأساسية للموقع.",
      reject: "رفض التحليلات",
      accept: "قبول التحليلات",
      privacy: "سياسة الخصوصية",
    },
  },
  ko: {
    ui: {
      previous: "이전",
      next: "다음",
      previousTestimonial: "이전 후기",
      nextTestimonial: "다음 후기",
      goToTestimonial: "후기로 이동",
      pageNotFound: "페이지를 찾을 수 없습니다",
      pageNotFoundBody: "찾으시는 페이지가 없거나 이동되었습니다.",
      backToHome: "홈으로 돌아가기",
    },
    uploader: {
      title: "여기에 파일을 업로드하세요",
      description: "드래그 앤 드롭하거나 클릭하여 선택하세요",
      supports: "지원",
      supportsAll: "모든 파일 형식 지원",
    },
    nav: {
      home: "홈",
      allTools: "모든 도구",
      blog: "블로그",
      about: "소개",
      faq: "FAQ",
      exploreTools: "도구 둘러보기",
      language: "언어",
    },
    cookie: {
      title: "쿠키 동의",
      body: "분석은 동의 후에만 활성화됩니다. 필수 저장소는 보안과 핵심 기능을 위해 계속 활성화됩니다.",
      reject: "분석 거부",
      accept: "분석 허용",
      privacy: "개인정보 처리방침",
    },
  },
  ja: {
    ui: {
      previous: "前へ",
      next: "次へ",
      previousTestimonial: "前のレビュー",
      nextTestimonial: "次のレビュー",
      goToTestimonial: "レビューへ移動",
      pageNotFound: "ページが見つかりません",
      pageNotFoundBody: "お探しのページは存在しないか、移動されました。",
      backToHome: "ホームに戻る",
    },
    uploader: {
      title: "ここにファイルをアップロード",
      description: "ドラッグ＆ドロップするかクリックして選択",
      supports: "対応",
      supportsAll: "すべてのファイル形式に対応",
    },
    nav: {
      home: "ホーム",
      allTools: "すべてのツール",
      blog: "ブログ",
      about: "概要",
      faq: "FAQ",
      exploreTools: "ツールを見る",
      language: "言語",
    },
    cookie: {
      title: "Cookie の同意",
      body: "解析は同意後にのみ有効になります。必要な保存領域はセキュリティと基本機能のために有効のままです。",
      reject: "解析を拒否",
      accept: "解析を許可",
      privacy: "プライバシーポリシー",
    },
  },
  ro: {
    ui: {
      previous: "Anterior",
      next: "Următor",
      previousTestimonial: "Testimonial anterior",
      nextTestimonial: "Următorul testimonial",
      goToTestimonial: "Mergi la testimonial",
      pageNotFound: "Pagina Nu A Fost Găsită",
      pageNotFoundBody: "Pagina pe care o cauți nu există sau a fost mutată.",
      backToHome: "Înapoi la pagina principală",
    },
    uploader: {
      title: "Încarcă fișierele aici",
      description: "Trage și plasează sau fă clic pentru a alege",
      supports: "Acceptă",
      supportsAll: "Acceptă toate tipurile de fișiere",
    },
    nav: {
      home: "Acasă",
      allTools: "Toate uneltele",
      blog: "Blog",
      about: "Despre",
      faq: "FAQ",
      exploreTools: "Explorează uneltele",
      language: "Limbă",
    },
    cookie: {
      title: "Consimțământ cookie",
      body: "Analiza este activată doar după acordul tău. Stocarea necesară rămâne activă pentru securitate și funcționarea de bază a site-ului.",
      reject: "Respinge analiza",
      accept: "Acceptă analiza",
      privacy: "Politica de confidențialitate",
    },
  },
};

function mergeDictionary(base: Dictionary, override: DeepPartial<Dictionary>): Dictionary {
  return {
    ui: { ...base.ui, ...override.ui },
    uploader: { ...base.uploader, ...override.uploader },
    nav: { ...base.nav, ...override.nav },
    footer: { ...base.footer, ...override.footer },
    cookie: { ...base.cookie, ...override.cookie },
    home: { ...base.home, ...override.home },
  };
}
